import { useMutation, useQuery } from '@apollo/client'
import { usePrompt, useToast } from '@ur-solutions/react-components'
import { useTranslate } from '@ur-solutions/react-hooks'
import { useMemo } from 'react'

import type { LeaderGroupForm, LeaderGroupNode } from '@/modules/companies'
import {
  ALL_LEADER_GROUPS_QUERY,
  ALL_MODULE_CONFIG_QUERYS_SHALLOW,
  CREATE_LEADER_GROUP,
  DELETE_LEADER_GROUP,
  LEADER_GROUP_RESTRICTABLE_MODULES,
  OldAddButton,
  PATCH_LEADER_GROUP,
} from '@/modules/companies'
import { SIMPLE_USERS_QUERY } from '@/modules/users/queries'
import { getNodes } from '@/utils/graphql'
import { useConfirm, usePermissions } from '@/utils/hooks'
import { PERMISSIONS } from '@/utils/permissions'
import { EditLeaderGroupPrompt } from './EditLeaderGroupPrompt'
import { PermissionGroupSection } from './PermissionGroupSection'

interface LeaderGroupsProps {}

const DISALLOWED_LEADER_GROUP_MODULES = new Set(['Handbook', 'Terminal'])

export const LeaderGroups: React.FC<LeaderGroupsProps> = () => {
  const translations = useTranslate({
    leaderGroups: 'companies.leader-groups',
    leaderGroupsDescription: 'companies.leader-groups-description',

    name: 'common.name',
    edit: 'common.edit',
    delete: 'common.delete',

    deleteLeaderGroup: 'companies.delete-leader-group',
    deleteLeaderGroupConfirm: 'companies.delete-leader-group-confirm',

    toast: {
      successCreate: 'companies.leader-group-create-success',
      successUpdate: 'companies.leader-group-update-success',
      successDelete: 'companies.leader-group-delete-success',
    },
  })

  const addPrompt = usePrompt()
  const addToast = useToast()
  const confirm = useConfirm()
  const { hasPermissions } = usePermissions()

  const canAdd = hasPermissions(PERMISSIONS.salary.add.timeaddon, true)

  const canEdit = hasPermissions(PERMISSIONS.salary.change.timeaddon, true)
  const canDelete = hasPermissions(PERMISSIONS.salary.delete.timeaddon, true)

  const { data, loading } = useQuery(ALL_LEADER_GROUPS_QUERY)

  const { data: moduleConfigData, loading: loadingModuleConfigs } = useQuery(
    ALL_MODULE_CONFIG_QUERYS_SHALLOW
  )
  const selectableModuleConfigs = useMemo(
    () =>
      (moduleConfigData?.allModuleConfigs ?? []).filter(
        c =>
          LEADER_GROUP_RESTRICTABLE_MODULES.includes(c.name as never) &&
          !DISALLOWED_LEADER_GROUP_MODULES.has(c.name)
      ),
    [moduleConfigData]
  )
  const selectableModuleConfigIds = useMemo(
    () => selectableModuleConfigs.map(c => c.id),
    [selectableModuleConfigs]
  )
  const selectableModuleConfigNames = useMemo(
    () => selectableModuleConfigs.map(c => c.name),
    [selectableModuleConfigs]
  )

  const { data: userData, loading: loadingUsers } = useQuery(
    SIMPLE_USERS_QUERY,
    {
      fetchPolicy: 'cache-and-network',
      nextFetchPolicy: 'cache-first',
      variables: {
        includeInactive: false,
      },
    }
  )

  const companyUsers = useMemo(
    () =>
      getNodes(userData?.allUsers).map(user => ({
        id: user.id,
        fullName: user.fullName,
      })) ?? [],
    [userData]
  )

  const refetchQueries = [
    {
      query: ALL_LEADER_GROUPS_QUERY,
    },
    'DriverActivityPeriodReport',
    'DriverActivityPeriodReportShallow',
    'AllDynamicColumnsQuery',
    'DriverActivityDayReport',
  ]

  const [createLeaderGroup] = useMutation(CREATE_LEADER_GROUP, {
    refetchQueries: refetchQueries,
    onCompleted: () => {
      addToast('success', translations.toast.successCreate)
    },
  })

  const [patchLeaderGroup] = useMutation(PATCH_LEADER_GROUP, {
    refetchQueries: refetchQueries,
    onCompleted: () => {
      addToast('success', translations.toast.successUpdate)
    },
  })

  const [deleteLeaderGroup] = useMutation(DELETE_LEADER_GROUP, {
    refetchQueries: refetchQueries,
    onCompleted: () => {
      addToast('success', translations.toast.successDelete)
    },
  })

  const leaderGroups = data?.allLeaderGroups ?? []

  function toExcludedModuleConfigIds(includedIds: string[]) {
    const includedSet = new Set(includedIds)
    return selectableModuleConfigIds.filter(id => !includedSet.has(id))
  }

  async function handleAdd() {
    const { data } = await addPrompt<LeaderGroupForm | null>(resolve => (
      <EditLeaderGroupPrompt
        initialData={{
          name: '',
          leaders: [],
          users: [],
          salaryGroup: null,
          department: null,
          includedModules: selectableModuleConfigIds,
        }}
        includedModuleNames={selectableModuleConfigNames}
        onSubmit={resolve}
        companyUsers={companyUsers}
      />
    ))

    if (!data) return

    void createLeaderGroup({
      variables: {
        input: {
          name: data.name,
          users: data.users,
          leaders: data.leaders,
          salaryGroup: data.salaryGroup,
          department: data.department,
          excludedModuleConfigs: toExcludedModuleConfigIds(
            data.includedModules
          ),
        },
      },
    })
  }

  async function handleEdit(leaderGroup: LeaderGroupNode) {
    const excludedModuleConfigIds = new Set(
      getNodes(leaderGroup.excludedModuleConfigs).map(config => config.id)
    )
    const initialData = {
      name: leaderGroup.name,
      users: leaderGroup.users.map(user => user.id),
      leaders: leaderGroup.leaders
        .filter(user => user.isActive) // Only count active users
        .map(user => user.id),
      salaryGroup: leaderGroup.salaryGroup?.id ?? null,
      department: leaderGroup.department?.id ?? null,
      includedModules: selectableModuleConfigs
        .filter(c => !excludedModuleConfigIds.has(c.id))
        .map(c => c.id),
    }
    const { data } = await addPrompt<LeaderGroupForm | null>(resolve => (
      <EditLeaderGroupPrompt
        initialData={initialData}
        includedModuleNames={selectableModuleConfigNames}
        companyUsers={companyUsers}
        onSubmit={resolve}
      />
    ))

    if (!data) return

    void patchLeaderGroup({
      variables: {
        id: leaderGroup.id,
        input: {
          name: data.name,
          users: data.users,
          leaders: data.leaders,
          salaryGroup: data.salaryGroup,
          department: data.department,
          excludedModuleConfigs: toExcludedModuleConfigIds(
            data.includedModules
          ),
        },
      },
    })
  }

  async function handleDelete(leaderGroup: LeaderGroupNode) {
    const { data } = await confirm(
      translations.deleteLeaderGroupConfirm,
      translations.deleteLeaderGroup
    )

    if (!data) return

    void deleteLeaderGroup({
      variables: {
        id: leaderGroup.id,
      },
    })
  }

  return (
    <PermissionGroupSection
      header={{
        title: translations.leaderGroups,
        description: translations.leaderGroupsDescription,
        loading: loading,
        children: (
          <>
            {!loading && !loadingUsers && !loadingModuleConfigs && canAdd && (
              <OldAddButton adding={false} onClick={handleAdd} />
            )}
          </>
        ),
      }}
      loading={loading}
      table={{
        rows: leaderGroups,
        handleEdit: handleEdit,
        handleDelete: handleDelete,
      }}
      canEdit={canEdit && !loadingModuleConfigs}
      canDelete={canDelete}
    />
  )
}
